import { Suspense, lazy } from 'react';
import { createRootRoute, createRoute, notFound, Outlet, Navigate } from '@tanstack/react-router';
import { NotFound } from './NotFound';
import { useDocumentParams, useIsExportRender } from 'app/router/params';
import { documentSearchSchema, editorSearchSchema, pdfSearchSchema, taggingSearchSchema } from 'app/router/search';
import { Title } from 'app/components';
import AuthWrapper from 'app/routing/AuthWrapper';
import PageWrapper from 'app/routing/PageWrapper';
import { SidebarContextProvider } from 'app/state/contexts/SidebarContext';
import { PermissionGuard } from './PermissionGuard';
import { SectionAccessDenied } from './SectionAccessDenied';
import { AdminGuard } from './AdminGuard';
import Auth from 'auth/Auth';

import Dashboard from 'features/dashboard/Dashboard';
import Projects from 'features/dashboard/Projects';
import Users from 'features/users/Users';
import { Organizations } from 'features/organizations/Organizations';
import MyProfile from 'features/my-profile/MyProfile';
import MyProfileOverview from 'features/my-profile/Overview';
import Insider from 'features/my-profile/insider/Insider';
import LabelRouter from 'features/admin-labels/LabelRouter';
import Labels from 'features/admin-labels/Labels';
import LabelEdit from 'features/admin-labels/LabelEdit';
import ListsRouter from 'features/admin-lists/ListsRouter';
import Lists from 'features/admin-lists/Lists';
import ListEdit from 'features/admin-lists/ListEdit';
import ListNew from 'features/admin-lists/ListNew';
import AdminUsersRouter from 'features/admin-users/UsersRouter';
import AdminTaxonomies from 'app/views/admin/taxonomies/Taxonomies';

import { DocumentEditor } from 'features/document-editor/DocumentEditor';
import { PdfViewer } from 'features/pdf-viewer/PdfViewer';
import { TemplateEditor } from 'features/template-editor/TemplateEditor';
import SideBySideEditor from 'features/compare-viewer/SideBySideViewer';

import Project from 'features/document-hub/Project';
import ProjectOverview from 'features/document-hub/Overview';
import Metadata from 'features/document-metadata/Metadata';
import { PermissionsLayout } from 'features/document-permissions/PermissionsLayout';
import SectionPermissions from 'features/document-permissions/SectionPermissions';
import DocumentPermissions from 'features/document-permissions/DocumentPermissions';
import { AccessLogs } from 'features/document-permissions/AccessLogs';
import TagPermissions from 'features/document-permissions/TagPermissions/TagPermissions';
import Pictures from 'features/document-pictures/Pictures';
import Locale from 'features/document-locale/Locale';
import ReferenceData from 'features/reference-data';
import { ReferenceList } from 'features/reference-data/components/ReferenceData';

import { DesignSettings } from 'features/design-settings';
import { ThemeConfiguration } from 'features/design-settings/components/ThemeConfiguration';
import { AreaSettings } from 'features/design-settings/components/AreaSettings';
import { PageNumbering } from 'features/design-settings/components/PageNumbering';
import { FontSettings } from 'features/design-settings/components/FontSettings';
import ProjectCSS from 'features/design-settings/utils/project-css';

import { TaggingSettings } from 'features/tagging/Tagging';
import { useTagDetailsModal } from 'features/tagging/TagDetailsModalContext';
import { Overview as TagOverview } from 'features/tagging/Overview/Overview';
import { Mandatory } from 'features/tagging/Mandatory/Mandatory';
import { Taxonomies } from 'features/tagging/Taxonomies/Taxonomies';
import { Categories } from 'features/tagging/Categories/Categories';
import { Extensions } from 'features/tagging/Extensions/Extensions';
import { TreeOverview } from 'features/tagging/TreeOverview/TreeOverview';
import { Export } from 'features/tagging/Export/Export';

const RouterDevtools = import.meta.env.DEV
  ? lazy(() => import('@tanstack/react-router-devtools').then((m) => ({ default: m.TanStackRouterDevtools })))
  : () => null;

const RootLayout = () => {
  const isExportRender = useIsExportRender();
  return (
    <>
      <Outlet />
      {!isExportRender && (
        <Suspense>
          <RouterDevtools />
        </Suspense>
      )}
    </>
  );
};

export const rootRoute = createRootRoute({ component: RootLayout });

const useDocumentId = () => useDocumentParams().documentId;
const useTaggingLeafProps = () => ({ documentId: useDocumentId(), onTagDetailsClick: useTagDetailsModal().showTagDetails });

const authRoute = createRoute({ getParentRoute: () => rootRoute, path: '/auth', component: Auth });

const appRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'app',
  notFoundComponent: NotFound,
  component: () => (
    <AuthWrapper>
      <SidebarContextProvider>
        <PageWrapper>
          <Outlet />
        </PageWrapper>
      </SidebarContextProvider>
    </AuthWrapper>
  ),
});

const dashboardRoute = createRoute({ getParentRoute: () => appRoute, id: 'dashboard', component: Dashboard });

const dashboardIndexRoute = createRoute({
  getParentRoute: () => dashboardRoute,
  path: '/',
  component: () => (
    <>
      <Title>Xplir Reporting Pilot - Overview</Title>
      <Projects />
    </>
  ),
});

const usersRoute = createRoute({ getParentRoute: () => dashboardRoute, path: '/users', component: Users });

const sectionRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/documents/$documentId/sections/$sectionId',
  validateSearch: editorSearchSchema,
  remountDeps: ({ params, search }) => ({ ...params, translation: search.translation, revision: search.revision, sectionRevision: search.sectionRevision }),
  component: () => {
    const { documentId, sectionId } = sectionRoute.useParams();
    return (
      <PermissionGuard
        documentId={documentId}
        sectionId={sectionId}
        requiredSectionPermissions={['COMMENT', 'WRITE']}
        fallback={<SectionAccessDenied documentId={documentId} />}
      >
        <Title>Xplir Reporting Pilot - Editor</Title>
        <DocumentEditor />
      </PermissionGuard>
    );
  },
});

const pdfRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/documents/$documentId/pdf/$documentRevisionId',
  validateSearch: pdfSearchSchema,
  component: () => (
    <>
      <Title>Xplir Reporting Pilot - PDF</Title>
      <PdfViewer />
    </>
  ),
});

const templateRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/documents/$documentId/templates/$templateId',
  component: () => {
    const { documentId } = templateRoute.useParams();
    return (
      <PermissionGuard documentId={documentId} requiredPermissions={['CODER']}>
        <Title>Xplir Reporting Pilot - Templates</Title>
        <TemplateEditor />
      </PermissionGuard>
    );
  },
});

const sideBySideRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/sbs/documents/$documentId/sections/$sectionId',
  validateSearch: editorSearchSchema,
  remountDeps: ({ params, search }) => ({ ...params, translation: search.translation, revision: search.revision, sectionRevision: search.sectionRevision }),
  component: () => {
    const { documentId, sectionId } = sideBySideRoute.useParams();
    return (
      <PermissionGuard documentId={documentId} sectionId={sectionId} requiredSectionPermissions={['COMMENT', 'WRITE']}>
        <Title>Xplir Reporting Pilot - Compare</Title>
        <SideBySideEditor />
      </PermissionGuard>
    );
  },
});

const documentRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/documents/$documentId',
  validateSearch: documentSearchSchema,
  beforeLoad: ({ params }) => {
    if (!/^\d+$/.test(params.documentId)) throw notFound();
  },
  component: () => (
    <>
      <Title>Xplir Reporting Pilot - Details</Title>
      <Project />
    </>
  ),
});

const documentIndexRoute = createRoute({ getParentRoute: () => documentRoute, path: '/', component: ProjectOverview });

const metadataRoute = createRoute({ getParentRoute: () => documentRoute, path: 'metadata', component: Metadata });

const documentPermissionsRoute = createRoute({
  getParentRoute: () => documentRoute,
  path: 'permissions',
  component: () => {
    const { documentId } = documentPermissionsRoute.useParams();
    return (
      <PermissionGuard documentId={documentId} requiredPermissions={['ADMIN']}>
        <PermissionsLayout />
      </PermissionGuard>
    );
  },
});

const documentPermissionsIndexRoute = createRoute({
  getParentRoute: () => documentPermissionsRoute,
  path: '/',
  component: () => {
    const { documentId } = documentPermissionsIndexRoute.useParams();
    return <Navigate to="/documents/$documentId/permissions/sections" params={{ documentId }} replace />;
  },
});

const sectionPermissionsRoute = createRoute({ getParentRoute: () => documentPermissionsRoute, path: 'sections', component: SectionPermissions });
const documentPermissionsListRoute = createRoute({ getParentRoute: () => documentPermissionsRoute, path: 'documents', component: DocumentPermissions });
const accessLogsRoute = createRoute({ getParentRoute: () => documentPermissionsRoute, path: 'access', component: AccessLogs });
const tagPermissionsRoute = createRoute({ getParentRoute: () => documentPermissionsRoute, path: 'tags', component: TagPermissions });

const picturesRoute = createRoute({
  getParentRoute: () => documentRoute,
  path: 'pictures',
  component: () => {
    const { documentId } = picturesRoute.useParams();
    return (
      <PermissionGuard documentId={documentId} requiredPermissions={['DESIGN', 'CODER']}>
        <Pictures />
      </PermissionGuard>
    );
  },
});

const referenceDataRoute = createRoute({
  getParentRoute: () => documentRoute,
  path: 'reference-data',
  component: () => {
    const { documentId } = referenceDataRoute.useParams();
    return (
      <PermissionGuard documentId={documentId} requiredPermissions={['DB']}>
        <ReferenceData />
      </PermissionGuard>
    );
  },
});

const referenceListRoute = createRoute({ getParentRoute: () => referenceDataRoute, path: '$refId', component: ReferenceList });

const localeRoute = createRoute({
  getParentRoute: () => documentRoute,
  path: 'language',
  component: () => {
    const { documentId } = localeRoute.useParams();
    return (
      <PermissionGuard documentId={documentId} requiredPermissions={['ADMIN']}>
        <Locale />
      </PermissionGuard>
    );
  },
});

const designRoute = createRoute({
  getParentRoute: () => documentRoute,
  path: 'design',
  component: () => {
    const { documentId } = designRoute.useParams();
    return (
      <PermissionGuard documentId={documentId} requiredPermissions={['DESIGN', 'CODER']}>
        <DesignSettings />
      </PermissionGuard>
    );
  },
});

const ThemeConfigurationView = () => <ThemeConfiguration documentId={useDocumentId()} />;
const AreaSettingsView = () => <AreaSettings documentId={useDocumentId()} />;
const PageNumberingView = () => <PageNumbering documentId={useDocumentId()} />;
const FontSettingsView = () => <FontSettings documentId={useDocumentId()} />;

const designIndexRoute = createRoute({ getParentRoute: () => designRoute, path: '/', component: ThemeConfigurationView });
const designAreasRoute = createRoute({ getParentRoute: () => designRoute, path: 'areas', component: AreaSettingsView });
const designPageNumberingRoute = createRoute({ getParentRoute: () => designRoute, path: 'page-numbering', component: PageNumberingView });
const designFontsRoute = createRoute({ getParentRoute: () => designRoute, path: 'fonts', component: FontSettingsView });
const designCssRoute = createRoute({
  getParentRoute: () => designRoute,
  path: 'css',
  component: () => {
    const { documentId } = designCssRoute.useParams();
    return (
      <PermissionGuard documentId={documentId} requiredPermissions={['CODER']}>
        <ProjectCSS documentId={documentId} />
      </PermissionGuard>
    );
  },
});

const taggingRoute = createRoute({
  getParentRoute: () => documentRoute,
  path: 'tagging',
  validateSearch: taggingSearchSchema,
  component: () => {
    const { documentId } = taggingRoute.useParams();
    return (
      <PermissionGuard documentId={documentId} requiredPermissions={['TAG_ADMIN']}>
        <TaggingSettings />
      </PermissionGuard>
    );
  },
});

const TagOverviewView = () => <TagOverview {...useTaggingLeafProps()} />;
const MandatoryView = () => <Mandatory {...useTaggingLeafProps()} />;
const TaxonomiesView = () => <Taxonomies documentId={useDocumentId()} />;
const CategoriesView = () => <Categories documentId={useDocumentId()} />;
const ExtensionsView = () => <Extensions {...useTaggingLeafProps()} />;
const TreeOverviewView = () => <TreeOverview documentId={useDocumentId()} />;
const ExportView = () => <Export {...useTaggingLeafProps()} />;

const taggingIndexRoute = createRoute({ getParentRoute: () => taggingRoute, path: '/', component: TagOverviewView });
const taggingMandatoryRoute = createRoute({ getParentRoute: () => taggingRoute, path: 'mandatory', component: MandatoryView });
const taggingTaxonomiesRoute = createRoute({ getParentRoute: () => taggingRoute, path: 'taxonomies', component: TaxonomiesView });
const taggingCategoriesRoute = createRoute({ getParentRoute: () => taggingRoute, path: 'categories', component: CategoriesView });
const taggingExtensionsRoute = createRoute({ getParentRoute: () => taggingRoute, path: 'extensions', component: ExtensionsView });
const taggingTreeRoute = createRoute({ getParentRoute: () => taggingRoute, path: 'tree', component: TreeOverviewView });
const taggingExportRoute = createRoute({ getParentRoute: () => taggingRoute, path: 'export', component: ExportView });

const organizationsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/organizations',
  component: () => (
    <>
      <Title>Xplir Reporting Pilot - Organization</Title>
      <Organizations />
    </>
  ),
});

const myProfileRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/myprofile',
  component: () => (
    <>
      <Title>Xplir Reporting Pilot - Min profil</Title>
      <MyProfile />
    </>
  ),
});
const myProfileIndexRoute = createRoute({ getParentRoute: () => myProfileRoute, path: '/', component: MyProfileOverview });
const myProfileInfoRoute = createRoute({ getParentRoute: () => myProfileRoute, path: 'info', component: Insider });

const adminLabelsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/admin/labels',
  component: () => (
    <>
      <Title>Xplir Reporting Pilot - Labels</Title>
      <LabelRouter />
    </>
  ),
});
const adminLabelsIndexRoute = createRoute({ getParentRoute: () => adminLabelsRoute, path: '/', component: Labels });
const adminLabelNewRoute = createRoute({ getParentRoute: () => adminLabelsRoute, path: 'new', component: () => <LabelEdit isNew /> });
const adminLabelEditRoute = createRoute({ getParentRoute: () => adminLabelsRoute, path: '$id', component: () => <LabelEdit /> });

const adminListsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/admin/lists',
  component: () => (
    <>
      <Title>Xplir Reporting Pilot - Lists</Title>
      <ListsRouter />
    </>
  ),
});
const adminListsIndexRoute = createRoute({ getParentRoute: () => adminListsRoute, path: '/', component: Lists });
const adminListNewRoute = createRoute({ getParentRoute: () => adminListsRoute, path: 'new', component: ListNew });
const adminListEditRoute = createRoute({ getParentRoute: () => adminListsRoute, path: '$id', component: ListEdit });

const adminUsersRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/admin/users',
  component: () => (
    <AdminGuard>
      <Title>Xplir Reporting Pilot - Users</Title>
      <AdminUsersRouter />
    </AdminGuard>
  ),
});

const adminTaxonomiesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/admin/taxonomies',
  component: () => (
    <>
      <Title>Xplir Reporting Pilot - Taxonomies</Title>
      <AdminTaxonomies />
    </>
  ),
});

export const routeTree = rootRoute.addChildren([
  authRoute,
  appRoute.addChildren([
    dashboardRoute.addChildren([dashboardIndexRoute, usersRoute]),
    sectionRoute,
    pdfRoute,
    templateRoute,
    sideBySideRoute,
    documentRoute.addChildren([
      documentIndexRoute,
      metadataRoute,
      documentPermissionsRoute.addChildren([
        documentPermissionsIndexRoute,
        sectionPermissionsRoute,
        documentPermissionsListRoute,
        accessLogsRoute,
        tagPermissionsRoute,
      ]),
      picturesRoute,
      referenceDataRoute.addChildren([referenceListRoute]),
      localeRoute,
      designRoute.addChildren([designIndexRoute, designAreasRoute, designPageNumberingRoute, designFontsRoute, designCssRoute]),
      taggingRoute.addChildren([
        taggingIndexRoute,
        taggingMandatoryRoute,
        taggingTaxonomiesRoute,
        taggingCategoriesRoute,
        taggingExtensionsRoute,
        taggingTreeRoute,
        taggingExportRoute,
      ]),
    ]),
    organizationsRoute,
    myProfileRoute.addChildren([myProfileIndexRoute, myProfileInfoRoute]),
    adminLabelsRoute.addChildren([adminLabelsIndexRoute, adminLabelNewRoute, adminLabelEditRoute]),
    adminListsRoute.addChildren([adminListsIndexRoute, adminListNewRoute, adminListEditRoute]),
    adminUsersRoute,
    adminTaxonomiesRoute,
  ]),
]);
